import React, { useMemo } from 'react';
import { Box, Typography } from '@material-ui/core';

/**
 * Recordings held by the carrier sit behind HTTP Basic auth, so an <audio>
 * tag pointed at one gets a 401 and renders as a dead player. Detect those
 * and say so instead.
 */
const isCarrierHosted = (url: string) => {
  const host = (url.match(/^https?:\/\/([^/?#]+)/i) || [])[1] || '';
  const hostname = host.split(':')[0].toLowerCase();
  return hostname === 'twilio.com' || hostname.endsWith('.twilio.com');
};

export const isPlayableRecordingUrl = (url?: string | null): boolean =>
  !!url && !isCarrierHosted(url);

type Props = {
  recordingUrl?: string | null;
  /** Shown when the recording cannot be played, so support can find it. */
  callSid?: string | null;
  width?: number;
};

/**
 * Audio for a phone survey, when there is one to play.
 *
 * Whether `recordingUrl` is a stored URL or a raw carrier link depends on what
 * the IVR service posts to saveIvrCallRecording, which stores it verbatim.
 * Both are handled here so neither produces a broken player.
 */
export const IvrRecording: React.FC<Props> = ({
  recordingUrl,
  callSid,
  width = 260,
}) => {
  const playable = useMemo(() => isPlayableRecordingUrl(recordingUrl), [
    recordingUrl,
  ]);

  if (!recordingUrl) {
    return null;
  }

  if (!playable) {
    return (
      <Box>
        <Typography variant="caption" color="textSecondary" component="div">
          Recording stored with the carrier &mdash; not playable here
        </Typography>
        {callSid && (
          <Typography variant="caption" color="textSecondary" component="div">
            {callSid}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <audio
      controls
      preload="none"
      src={recordingUrl}
      style={{ width, maxWidth: '100%', height: 32 }}
    />
  );
};

export default React.memo(IvrRecording);
